//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import BusinessIcon from '@mui/icons-material/Business'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  InputLabel,
  Modal,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'

import { modalStyle } from '@react/components/ButtonModal'
import { companyProfileSignedAction, fetchCompanyProfileSigned } from '@react/fetchers/companyProfileFetchers'
import { chainActions } from '@react/redux/chainSlice'
import { poolActions } from '@react/redux/poolSlice'
import { useOtChainState, useOtDispatch } from '@react/redux/store'
import { CompanyProfileActionCTO, CompanyProfileActionEnum, CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { MESSAGE_TO_SIGN } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'
import { ethers } from 'ethers'
import React, { ReactElement, useEffect, useState } from 'react'
import * as yup from 'yup'
import CompanyProfileSelfRegistryForm from './CompanyProfileSelfRegistryForm'

export default function CompanyProfileLenderView(): ReactElement {
  const theme = useTheme()
  const {
    stageConfiguration: { serverApiBaseUrl, restAPIKey },

    userConnection: { accountAddr, companyProfile, signedMessage, signer },
  } = useOtChainState()
  const dispatch = useOtDispatch()

  // const [checkedCompanyProfile, setCheckedCompanyProfile] = useState(false)
  const [newCompanyProfile, setNewCompanyProfile] = useState<CompanyProfileCTO | undefined>(undefined)
  const [openModal, setOpenModal] = React.useState(false)
  const [emailAdd, setEmailAdd] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSigning, setIsSigning] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [errorSigning, setErrorSigning] = useState(false)
  let localIsSigning = false
  useEffect(() => {
    const asyncSignMessage = async () => {
      logOT.info('asyncSignMessage 1', { signedMessage, signer, isSigning })
      setIsSigning(true)
      try {
        if (signer) {
          logOT.info('SignMessage ,CompanyProfileLenderView 1')
          const _signedMessage = await signer.signMessage(MESSAGE_TO_SIGN())
          logOT.info('asyncSignMessage 2,_signedMessage', _signedMessage)
          dispatch(chainActions.updateSignedMessage(_signedMessage))
        }
        setIsSigning(false)
        logOT.info('asyncSignMessage 3', { signedMessage, signer, isSigning })
      } catch (_error) {
        setErrorSigning(true)
        setIsSigning(false)
        logOT.info('asyncSignMessage 4', { signedMessage, signer, isSigning })
      }
    }
    if (!signedMessage && signer && !localIsSigning && !isSigning && !errorSigning) {
      localIsSigning = true

      asyncSignMessage().finally(() => {
        localIsSigning = false
      })
    }
  }, [signedMessage, isSigning])
  useEffect(() => {
    logOT.info('CompanyProfileLenderView useEffect', { companyProfile, accountAddr, signedMessage })
    if (!companyProfile && accountAddr && signedMessage) {
      const asyncFetchCompanyProfileSigned = async () => {
        setFetching(true)
        const _companyProfile = await fetchCompanyProfileSigned(
          serverApiBaseUrl,
          restAPIKey,
          accountAddr,
          signedMessage
        )
        logOT.debug('AppKitListener _companyProfile', { _companyProfile })
        if (_companyProfile) {
          dispatch(chainActions.updateCompanyProfile(_companyProfile))
        }
        setFetching(false)
      }
      asyncFetchCompanyProfileSigned().finally(() => {
        setFetching(false)
      })
    }
  }, [signedMessage, companyProfile, accountAddr])
  // useEffect(() => {
  //   logOT.debug('AppKitListener embeddedWalletInfo', { checkedCompanyProfile })
  //   if (!checkedCompanyProfile && accountAddr) {
  //     fetchCompanyProfileExists(serverApiBaseUrl, restAPIKey, accountAddr).then((exists) => {
  //       if (exists) {
  //         setCheckedCompanyProfile(true)
  //       } else {
  //         setOpenModal(true)
  //       }
  //     })
  //   }
  // }, [checkedCompanyProfile, accountAddr])

  useEffect(() => {
    logOT.info('CompanyProfileLenderView useEffect', { companyProfile, accountAddr, signedMessage, isSigning })
    if (
      (!companyProfile?.uuid || companyProfile.uuid.length == 0) &&
      accountAddr &&
      signedMessage &&
      !isSigning &&
      location.hostname.startsWith('app')
    ) {
      setNewCompanyProfile({
        uuid: ethers.uuidV4(ethers.randomBytes(16)),
        companyName: '',
        emails: [],
        wallets: [accountAddr],
      })
      logOT.info('Open Model 1')
      setOpenModal(true)
    }
  }, [companyProfile, accountAddr, signedMessage, isSigning])

  const handleClose = (): void => {
    setOpenModal(false)
  }
  let validEmail = false
  if (emailAdd.length > 0) {
    const schema = yup.string().email().required()
    try {
      schema.validateSync(emailAdd)
      validEmail = true
    } catch (_error) {
      validEmail = false
    }
  }
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleAddEmail = (): void => {
    logOT.debug('AppKitListener handleAddEmail')
    const handleAddEmailAsync = async () => {
      if (validEmail && companyProfile && signedMessage && accountAddr && serverApiBaseUrl && restAPIKey) {
        setIsSubmitted(true)
        const companyProfileActionCTO: CompanyProfileActionCTO = {
          action: CompanyProfileActionEnum.ADD_EMAIL,
          emails: [emailAdd],
          uuid: companyProfile.uuid,
        }
        logOT.info('companyProfileActionCTO', { companyProfileActionCTO, signedMessage })
        const savedProfile = await companyProfileSignedAction(
          serverApiBaseUrl,
          companyProfileActionCTO,
          restAPIKey,
          signedMessage,
          accountAddr
        )
        logOT.info('savedProfile', { savedProfile })
        // Update Redux store with new company profile
        dispatch(chainActions.updateCompanyProfile(savedProfile))

        dispatch(poolActions.updateCompanyProfile(savedProfile))
        setIsSubmitted(false)
        setEmailAdd('')
      }
    }
    handleAddEmailAsync()
  }

  if (errorSigning) {
    return <div>Prevented from signing message with your wallet</div>
  }
  //  const { poolAccountAccessPairs } = useOtSelector((state) => state.poolState) // TODO User this it get the list of pool the company has access to
  if (fetching) {
    return <CircularProgress />
  }

  if (!companyProfile) {
    return (
      <Paper
        data-testid="CompanyProfileView"
        sx={{
          marginTop: '20px',
          marginBottom: 4,
          marginRight: '2rem',
          marginLeft: '2rem',
          padding: '24px',
          borderRadius: 4,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Company Profile Not found
        </Typography>
        <Modal
          open={openModal}
          data-testid="modal"
          onClose={() => {
            logOT.debug('AppKitListener setNewCompanyProfile setOpenModal4 false')
            setOpenModal(false)
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Paper sx={modalStyle}>
            <Typography
              align="left"
              sx={{ display: 'inline', float: 'left', fontWeight: 'bold', marginLeft: 4 }}
              variant={'h4'}
            >
              Register YourCompany Profile
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{ height: '14px', width: '14px', padding: 0, float: 'right', color: theme.palette.primary.main }}
            >
              <CloseIcon sx={{ width: '12px', height: '12px', marginTop: 0, color: theme.palette.fg.muted }} />
            </IconButton>
            <Box sx={{ marginTop: '32px', marginLeft: 2 }}>
              <CompanyProfileSelfRegistryForm
                companyProfileCTO={newCompanyProfile}
                companyProfiles={[]}
                accountAddr={accountAddr ?? ''}
                closeCallback={handleClose}
              />
            </Box>
          </Paper>
        </Modal>
      </Paper>
    )
  }

  return (
    <Box
      data-testid="CompanyProfileView"
      sx={{
        marginTop: '20px',
        marginBottom: 4,
        marginRight: '2rem',
        marginLeft: '2rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {/* Overview Section */}
      <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[200],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BusinessIcon sx={{ color: theme.palette.grey[600] }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                  {companyProfile.companyName}
                </Typography>
                <Grid item xs={12} key={'uuid2'}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      alignContent={'left'}
                      variant="bodyBold"
                      gutterBottom
                      component="div"
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      {companyProfile.uuid}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(companyProfile.uuid)}
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              </Box>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} key={'originatorUuid'}>
              <Typography alignContent={'left'} variant="bodyBold" gutterBottom component="div">
                Originator UUID: {companyProfile.originatorUuid ?? 'Not Set'}
              </Typography>
            </Grid>
            <Grid item xs={12} key={'referralCreationAccessCode'}>
              <Typography alignContent={'left'} variant="bodyBold" gutterBottom component="div">
                Referral Code: {companyProfile.referralCreationAccessCode ?? 'Not Set'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Emails Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                {companyProfile.emails.map((email, index) => (
                  <Typography
                    key={`email-${index}`}
                    variant="body1"
                    sx={{
                      p: 1.5,
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: 1,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      fontFamily: 'monospace',
                    }}
                  >
                    {email}
                  </Typography>
                ))}
              </Box>

              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <InputLabel htmlFor="emailAdd">Email</InputLabel>
                <TextField
                  id="emailAdd"
                  data-testid="emailAdd"
                  sx={{ width: '300px' }}
                  variant="outlined"
                  placeholder="Enter Email"
                  value={emailAdd}
                  onChange={(event) => {
                    setEmailAdd(event.target.value)
                  }}
                />

                <Button
                  variant="contained"
                  color="primary"
                  disabled={!validEmail || isSubmitted}
                  onClick={handleAddEmail}
                >
                  Add Email
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Connected Wallets Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AccountBalanceWalletIcon sx={{ color: theme.palette.grey[600] }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Connected Wallets
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                {companyProfile.wallets.map((wallet, index) => (
                  <Box key={`wallet-${index}`} display="flex" alignItems="center" justifyContent="space-between">
                    <Typography
                      variant="body1"
                      sx={{
                        p: 1.5,
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        flex: 1,
                        mr: 1,
                      }}
                    >
                      {wallet}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(wallet)}
                      sx={{ color: theme.palette.grey[600] }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {companyProfile.wallets.length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      textAlign: 'center',
                      py: 2,
                    }}
                  >
                    No wallets connected
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
